// The following product related interfaces are all redefined here so that
// sortOptionsAndVariations is reusable by both IProduct and
// IRetailerProduct.
interface ISortableProductVariationMapEntry {
  name?: string;
  entry_values?: ISortableProductVariationMapEntryValue[];
}

interface ISortableProductVariationMapEntryValue {
  value?: string;
}

interface ISortableProductVariationMap {
  entries?: ISortableProductVariationMapEntry[];
}

interface ISortableProductVariation {
  name?: string;
  value?: string;
}

interface ISortableProductOption {
  variations?: ISortableProductVariation[];
}

interface ISortableProduct {
  variation_map?: ISortableProductVariationMap;
  options?: ISortableProductOption[];
}

/**
 * To get around backend option indexing being an issue
 * Sorts product options and their variation lists based on the variation_map
 * of the product. Operates in place on a product.
 */
export const sortOptionsAndVariations = (product: ISortableProduct): void => {
  // To get around indexing being an issue on the BE
  const entries = product.variation_map?.entries;
  if (entries === undefined) {
    return;
  }

  product.options?.forEach((option) => {
    option.variations?.sort(
      (first: ISortableProductVariation, second: ISortableProductVariation) => {
        return (
          entries.findIndex((entry) => entry.name === first.name) -
          entries.findIndex((entry) => entry.name === second.name)
        );
      }
    );
  });

  product.options?.sort(
    (first: ISortableProductOption, second: ISortableProductOption) => {
      if (
        !first.variations ||
        !second.variations ||
        first.variations.length !== second.variations.length
      ) {
        return 0;
      }

      for (let i = 0; i < (first.variations.length || 0); i++) {
        const firstVariation = first.variations[i];
        const secondVariation = second.variations[i];

        // This should never happen since we first sort the variations
        // and a valid option should have exactly one variation
        // for every variant type
        if (firstVariation?.name !== secondVariation?.name) {
          return 0;
        }

        const variantOrder = entries.find(
          (entry) => entry.name === firstVariation?.name
        )?.entry_values;

        // This should also never happen, if one variant has an ordering
        // then all should have an ordering
        if (variantOrder === undefined) {
          continue;
        }

        const orderValue =
          variantOrder.findIndex(
            (entry) => entry.value === firstVariation?.value
          ) -
          variantOrder.findIndex(
            (entry) => entry.value === secondVariation?.value
          );

        // Only want to return the value if it isn't 0, otherwise we move
        // on to the next variant to see if it can be the determining factor
        if (orderValue !== 0) {
          return orderValue;
        }
      }

      return 0;
    }
  );
};
